import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import MediaRenderer from "../../components/MediaRenderer";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { uploadAdminMedia } from "../../lib/mediaUpload";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { mergeSiteContent } from "../../utils/helpers";

const mediaFields = [
  { label: "Accueil - News vidéo", path: "pages.home.newsHero.video", type: "video", folder: "site/home/news" },
  { label: "Accueil - News fallback", path: "pages.home.newsHero.fallbackImage", type: "image", folder: "site/home/news" },
  { label: "Accueil - logo Unlimited", path: "pages.home.sessionLogo", type: "image", folder: "site/home" },
  { label: "Studio - vidéo hero", path: "pages.studio.heroVideo", type: "video", folder: "site/studio" },
  { label: "Studio - image fallback", path: "pages.studio.heroImage", type: "image", folder: "site/studio" },
  { label: "Contact - image hero", path: "pages.contact.heroImage", type: "image", folder: "site/contact" },
];

function getIn(obj, path) {
  return path.split(".").reduce((value, key) => value?.[key], obj) || "";
}

function setIn(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cursor = clone;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return clone;
}

function MediaField({ item, value, onSave }) {
  const [draft, setDraft] = useState(value);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  useEffect(() => setDraft(value), [value]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAdminMedia(file, item.folder);
      setDraft(url);
      await onSave(item.path, url);
      toast.success("Média mis à jour");
    } catch (err) {
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <article className="rounded-lg border border-white/10 bg-black p-4">
      <MediaRenderer
        src={draft}
        type={item.type}
        alt=""
        background
        className="aspect-video w-full rounded object-cover"
      />
      <h2 className="mt-4 text-lg font-bold">{item.label}</h2>
      <input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => onSave(item.path, draft)}
        className="mt-3 w-full rounded border border-white/10 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-red-500"
      />
      <label className={`mt-3 inline-flex cursor-pointer rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black ${uploading ? "pointer-events-none opacity-60" : ""}`}>
        {uploading ? "Upload..." : `Importer ${item.type === "video" ? "vidéo" : "image"}`}
        <input
          type="file"
          accept={item.type === "video" ? "video/*" : "image/*"}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            upload(file);
          }}
        />
      </label>
    </article>
  );
}

export default function AdminMedias() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [content, setContent] = useState(() => mergeSiteContent());
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      setContent(mergeSiteContent(snap.exists() ? snap.data() : {}));
    });
  }, [isAdmin]);

  const savePath = async (path, value) => {
    const next = setIn(content, path, value);
    setContent(next);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(next), { merge: true });
      toast.success("Média sauvegardé");
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Médias</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">Changer de compte</button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">Connexion Google</button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Médias globaux</h1>
            <p className="mt-2 text-sm text-gray-400">Les médias principaux réutilisés dans le site.</p>
          </div>
          <Link to="/admin" className="w-fit rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Dashboard</Link>
        </header>
        <section className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {mediaFields.map((item) => (
            <MediaField key={item.path} item={item} value={getIn(content, item.path)} onSave={savePath} />
          ))}
        </section>
      </div>
    </main>
  );
}
